import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Modal, Alert } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faSparkles } from '@fortawesome/pro-solid-svg-icons'
import { useTranslationRankmi } from 'helpers/translations.helper'
import {
  previewAiEvaluation,
  AiEvaluationQuestion,
  AiEvaluationPreviewMeta,
} from 'api/administration/lessons/aiEvaluation'
import { IQuestionBankData } from 'interfaces/questionBank'
import GenerationStepper from './GenerationStepper'
import EvaluationPreviewContent from './EvaluationPreviewContent'
import EvaluationErrorContent from './EvaluationErrorContent'
import RegenerateAllConfirmModal from './RegenerateAllConfirmModal'
import {
  reorderQuestions,
  validateQuestions,
  withClientIds,
} from './questionUtils'
import { aiPreviewToQuestionBank } from './aiToQuestionBank'
import AiGradientDefs from './AiGradientDefs'
import {
  AI_GENERATION_MODAL_WIDTH,
  AI_ERROR_MODAL_WIDTH,
  AI_PREVIEW_MODAL_WIDTH,
} from './aiEvaluationTokens'
import {
  AI_EVALUATION_MODAL_WRAP,
  AiEvaluationModalGlobalStyles,
  GeneratingTitleIcon,
  GeneratingTitleRow,
  GeneratingTitleSparkle,
  ModalAlertWrap,
} from './AiEvaluationModal.styled'

type Phase = 'generating' | 'preview' | 'error'

type AiErrorKind =
  | 'flagDisabled'
  | 'noContent'
  | 'serviceUnavailable'
  | 'notConfigured'
  | 'generationFailed'
  | 'invalidQuestions'

// La configuración de la evaluación (nombre, descripción, puntaje de aprobación,
// reintentos) se define en el editor del Banco de Preguntas, igual que en el flujo
// sin IA. El modal solo genera y deja editar las preguntas, con una cantidad fija.
const DEFAULT_QUESTION_COUNT = 5
const GENERATING_STEP_ADVANCE_MS = 2000

// Mapea la respuesta del backend (status + code) a un mensaje de UX consistente,
// independiente del idioma del mensaje crudo y sin filtrar el proveedor (Rankmi IA / OpenAI).
const resolveAiErrorKind = (
  err: any,
  fallback: AiErrorKind = 'generationFailed'
): AiErrorKind => {
  const status = err?.response?.status
  const code = err?.response?.data?.code
  if (status === 403 || code === 'ai_evaluation_disabled_for_tenant')
    return 'flagDisabled'
  if (code === 'ai_lesson_empty_content') return 'noContent'
  if (code === 'ai_service_timeout' || status === 503)
    return 'serviceUnavailable'
  if (code === 'ai_service_unavailable') return 'notConfigured'
  if (code === 'ai_generation_failed') return 'generationFailed'
  return fallback
}

interface Props {
  visible: boolean
  lessonId: number | string
  lessonName?: string
  richTextCount: number
  onClose: () => void
  onConfirm: (data: IQuestionBankData) => void
}

export const AiEvaluationModal: React.FC<Props> = ({
  visible,
  lessonId,
  richTextCount,
  onClose,
  onConfirm,
}) => {
  const t = useTranslationRankmi({
    prefix: 'administration.blocks.aiEvaluation',
  })

  const [phase, setPhase] = useState<Phase>('generating')
  const [generatingStep, setGeneratingStep] = useState(0)
  const [questions, setQuestions] = useState<AiEvaluationQuestion[]>([])
  const [meta, setMeta] = useState<AiEvaluationPreviewMeta | null>(null)
  const [errorKind, setErrorKind] = useState<AiErrorKind | null>(null)
  const [questionsDirty, setQuestionsDirty] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [regenerateAllConfirmVisible, setRegenerateAllConfirmVisible] =
    useState(false)

  const errorMessage = errorKind ? t(`errors.${errorKind}`) : null

  const generationId = useRef(0)
  const stepTimersRef = useRef<number[]>([])

  const clearGeneratingStepTimers = useCallback(() => {
    stepTimersRef.current.forEach((timerId) => window.clearTimeout(timerId))
    stepTimersRef.current = []
  }, [])

  const scheduleGeneratingSteps = useCallback(
    (genId: number) => {
      clearGeneratingStepTimers()
      setGeneratingStep(0)

      const toStep2 = window.setTimeout(() => {
        if (generationId.current !== genId) return
        setGeneratingStep(1)

        const toStep3 = window.setTimeout(() => {
          if (generationId.current !== genId) return
          setGeneratingStep(2)
        }, GENERATING_STEP_ADVANCE_MS)

        stepTimersRef.current.push(toStep3)
      }, GENERATING_STEP_ADVANCE_MS)

      stepTimersRef.current = [toStep2]
    },
    [clearGeneratingStepTimers]
  )

  const resetState = useCallback(() => {
    setPhase('generating')
    setGeneratingStep(0)
    setQuestions([])
    setMeta(null)
    setErrorKind(null)
    setQuestionsDirty(false)
    setName('')
    setDescription('')
    setRegenerateAllConfirmVisible(false)
  }, [])

  const currentQuestionNames = (list: AiEvaluationQuestion[]) =>
    list.map((q) => q.name.trim()).filter(Boolean)

  const runPreview = useCallback(
    async (options?: {
      isRegenerate?: boolean
      excludeQuestions?: string[]
    }) => {
      const currentGen = ++generationId.current
      setPhase('generating')
      setErrorKind(null)
      scheduleGeneratingSteps(currentGen)
      if (!options?.isRegenerate) {
        setQuestionsDirty(false)
      }

      try {
        const { data } = await previewAiEvaluation(lessonId, {
          questionCount: DEFAULT_QUESTION_COUNT,
          excludeQuestions: options?.excludeQuestions,
        })
        if (currentGen !== generationId.current) return

        setQuestions(withClientIds(data.questions || []))
        setMeta(data.meta || { blockCount: richTextCount })

        const suggestedName = data.meta?.suggestedName
        const suggestedDescription = data.meta?.suggestedDescription
        setName(suggestedName || '')
        setDescription(suggestedDescription || '')

        setPhase('preview')
        if (options?.isRegenerate) {
          setQuestionsDirty(false)
        }
      } catch (err: any) {
        if (currentGen !== generationId.current) return
        setErrorKind(resolveAiErrorKind(err, 'generationFailed'))
        setPhase('error')
      }
    },
    [lessonId, richTextCount, scheduleGeneratingSteps]
  )

  useEffect(() => {
    if (!visible) {
      clearGeneratingStepTimers()
      return
    }
    resetState()
    runPreview()
  }, [visible, resetState, runPreview, clearGeneratingStepTimers])

  useEffect(
    () => () => clearGeneratingStepTimers(),
    [clearGeneratingStepTimers]
  )

  const handleClose = () => {
    generationId.current += 1
    clearGeneratingStepTimers()
    onClose()
  }

  const handleRegenerateAll = () => {
    const doRegenerate = () =>
      runPreview({
        isRegenerate: true,
        excludeQuestions: currentQuestionNames(questions),
      })
    if (!questionsDirty) {
      doRegenerate()
      return
    }
    setRegenerateAllConfirmVisible(true)
  }

  const handleConfirmRegenerateAll = () => {
    setRegenerateAllConfirmVisible(false)
    void runPreview({
      isRegenerate: true,
      excludeQuestions: currentQuestionNames(questions),
    })
  }

  const deleteQuestion = (clientId: string) => {
    setQuestionsDirty(true)
    setQuestions((prev) =>
      reorderQuestions(prev.filter((q) => q.clientId !== clientId))
    )
  }

  const regenerateOne = async (clientId: string) => {
    const target = questions.find((q) => q.clientId === clientId)
    if (!target) return

    setQuestions((prev) =>
      prev.map((q) =>
        q.clientId === clientId ? { ...q, regenerating: true } : q
      )
    )

    try {
      const { data } = await previewAiEvaluation(lessonId, {
        questionCount: 1,
        excludeQuestions: currentQuestionNames(questions),
      })
      const replacement = data.questions?.[0]
      if (!replacement) {
        throw new Error('missing question')
      }
      const fresh = { ...replacement, clientId, regenerating: false }
      setQuestions((prev) =>
        reorderQuestions(prev.map((q) => (q.clientId === clientId ? fresh : q)))
      )
      setQuestionsDirty(true)
    } catch (err: any) {
      setQuestions((prev) =>
        prev.map((q) =>
          q.clientId === clientId ? { ...q, regenerating: false } : q
        )
      )
      setErrorKind(resolveAiErrorKind(err, 'generationFailed'))
    }
  }

  // No persiste: vuelca las preguntas al editor del Banco de Preguntas v2 con el
  // nombre/descripción (sugeridos por la IA y editables aquí). El resto de la
  // configuración la define la persona en el editor, igual que en el flujo manual.
  const handleConfirm = () => {
    const validation = validateQuestions(questions)
    if (validation) {
      setErrorKind('invalidQuestions')
      return
    }

    setErrorKind(null)
    const data = aiPreviewToQuestionBank(reorderQuestions(questions), {
      name: name.trim(),
      description: description.trim(),
    })
    onConfirm(data)
    handleClose()
  }

  const blockCount = meta?.blockCount ?? richTextCount
  const validationError = validateQuestions(questions)
  const isGenerating = phase === 'generating'
  const modalWrapClassName = [
    AI_EVALUATION_MODAL_WRAP,
    phase === 'preview' ? 'ai-evaluation-modal-wrap--preview' : '',
    phase === 'error' ? 'ai-evaluation-modal-wrap--error' : '',
  ]
    .filter(Boolean)
    .join(' ')

  const modalTitle = (
    <GeneratingTitleRow>
      {t('generationModal.title')}
      <GeneratingTitleIcon aria-hidden>
        <GeneratingTitleSparkle icon={faSparkles} />
      </GeneratingTitleIcon>
    </GeneratingTitleRow>
  )

  return (
    <>
      <AiGradientDefs />
      <AiEvaluationModalGlobalStyles />
      <Modal
        visible={visible}
        onCancel={handleClose}
        footer={null}
        width={
          isGenerating
            ? AI_GENERATION_MODAL_WIDTH
            : phase === 'error'
            ? AI_ERROR_MODAL_WIDTH
            : AI_PREVIEW_MODAL_WIDTH
        }
        title={modalTitle}
        closeIcon={<FontAwesomeIcon icon={faTimes as IconProp} />}
        destroyOnClose
        wrapClassName={modalWrapClassName}
      >
        {errorMessage && phase !== 'error' && (
          <ModalAlertWrap role="alert">
            <Alert
              type="error"
              message={errorMessage}
              closable
              onClose={() => setErrorKind(null)}
            />
          </ModalAlertWrap>
        )}

        {phase === 'generating' && (
          <GenerationStepper
            blockCount={blockCount}
            activeStep={generatingStep}
          />
        )}

        {phase === 'error' && errorKind === 'flagDisabled' && (
          <EvaluationErrorContent
            variant="flagDisabled"
            title={t('errors.flagDisabledTitle')}
            message={t('errors.flagDisabledBody')}
            onCancel={handleClose}
          />
        )}

        {phase === 'error' && errorKind !== 'flagDisabled' && errorKind && (
          <EvaluationErrorContent
            variant="recoverable"
            errorKind={
              errorKind === 'noContent' ||
              errorKind === 'serviceUnavailable' ||
              errorKind === 'notConfigured'
                ? errorKind
                : 'generationFailed'
            }
            onCancel={handleClose}
            onRetry={() => runPreview()}
          />
        )}

        {phase === 'preview' && (
          <EvaluationPreviewContent
            questions={questions}
            blockCount={blockCount}
            meta={meta}
            name={name}
            description={description}
            validationError={!!validationError}
            errorMessage={errorMessage}
            onDismissError={() => setErrorKind(null)}
            onDeleteQuestion={deleteQuestion}
            onRegenerateQuestion={regenerateOne}
            onCancel={handleClose}
            onRegenerateAll={handleRegenerateAll}
            onConfirm={handleConfirm}
          />
        )}
      </Modal>
      <RegenerateAllConfirmModal
        visible={regenerateAllConfirmVisible}
        onCancel={() => setRegenerateAllConfirmVisible(false)}
        onConfirm={handleConfirmRegenerateAll}
      />
    </>
  )
}

export default AiEvaluationModal
